/**
 * Widget Loader Script — Entry Point
 *
 * Executed via:
 *   <script src="...widget.{hash}.js" data-widget-key="{key}" defer></script>
 *
 * Initialization sequence:
 *   1. Read data-widget-key from the script element
 *   2. Extract __wpreview token (if present), then strip from URL
 *   3. Fetch widget config from CDN: /o/{orgId}/w/{widgetId}/config.json
 *   4. Evaluate pageAllowList + pageBlockList against current location
 *   5. POST /session (or /session/preview if preview token present) — on 403/404: exit silently
 *   6. Inject iframe + launcher; wire click handler
 *   7. Bridge loader ↔ iframe via postMessage
 *   8. Schedule session refresh at 80% of TTL
 *
 * Single-instance: if this loader already ran on the page, abort.
 *
 * ETHOS-964
 */

import { fetchWidgetConfig } from "./configFetcher.js";
import { isPageAllowed } from "./pagePolicyEvaluator.js";
import { requestSession, refreshSession } from "./sessionClient.js";
import type { SessionResponse } from "./sessionClient.js";
import { injectWidget } from "./iframeInjector.js";
import { loadState, saveState, clearState } from "./stateStore.js";

// Single-instance guard — prevent double initialization if two script tags load.
const INIT_FLAG = "__ethos_widget_initialized__";
if ((window as unknown as Record<string, unknown>)[INIT_FLAG]) {
  // Already initialized — abort silently.
} else {
  (window as unknown as Record<string, unknown>)[INIT_FLAG] = true;
  void init();
}

async function init(): Promise<void> {
  // 1. Locate the loader script element and read data-widget-key.
  // document.currentScript is null for defer/async scripts, so fall back
  // to querying for the script element by its data attribute.
  const scriptEl =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-widget-key]");
  const widgetKey = scriptEl?.getAttribute("data-widget-key");
  if (!widgetKey) {
    return;
  }

  // Resolve initial bases from script src origin.
  // data-api-base is an optional override for local dev where ports differ.
  const scriptSrc = scriptEl?.src ?? "";
  const scriptOrigin = scriptSrc ? new URL(scriptSrc).origin : window.location.origin;
  const initialApiBase = scriptEl?.getAttribute("data-api-base") ?? scriptOrigin;
  const cdnBase = scriptOrigin;

  // 2. Extract __wpreview token (if present) before stripping it from the URL.
  const previewToken = extractPreviewToken();
  stripPreviewParam();

  // Resolve orgId + widgetId from the widget key.
  // data-org-id / data-widget-id are optional overrides for local dev.
  const explicitOrgId = scriptEl?.getAttribute("data-org-id");
  const explicitWidgetId = scriptEl?.getAttribute("data-widget-id");
  const resolved = (explicitOrgId && explicitWidgetId)
    ? { orgId: explicitOrgId, widgetId: explicitWidgetId }
    : resolveWidgetKey(widgetKey);
  if (!resolved) {
    return;
  }
  const { orgId, widgetId } = resolved;

  // 3. Fetch config (API first, CDN blob fallback).
  const config = await fetchWidgetConfig(initialApiBase, orgId, widgetId, cdnBase);
  if (!config) {
    return;
  }

  if (config.status !== "active" && config.status !== "pending-approval") {
    return;
  }

  // Resolve final URLs — prefer config response values, fall back to data attributes / script origin.
  const apiBase = config.apiBaseUrl || initialApiBase;
  const adminBase = config.adminBaseUrl || scriptEl?.getAttribute("data-admin-base") || "";

  // 4. Evaluate page allow/block policy.
  const currentPath = window.location.pathname;
  if (!isPageAllowed(currentPath, config.pageAllowList, config.pageBlockList)) {
    return;
  }

  // 5. Obtain session token (reuse persisted session if still valid).
  const persisted = loadState(orgId, widgetId);
  let session: SessionResponse | null = null;
  let serviceUnavailable = false;

  const isPreviewNow = Boolean(previewToken);
  const hasValidPersistedSession = persisted?.session &&
    new Date(persisted.session.expiresAt).getTime() > Date.now() + 60_000 &&
    // Don't reuse a preview session on a normal load (or vice versa).
    Boolean(persisted.session.canPreview) === isPreviewNow;

  if (hasValidPersistedSession) {
    session = persisted!.session;
  } else {
    const SESSION_RETRIES = 2;
    const SESSION_BACKOFF = [1000, 2000];
    for (let attempt = 0; attempt <= SESSION_RETRIES; attempt++) {
      try {
        session = await requestSession(apiBase, orgId, widgetId, previewToken ?? undefined);
        break;
      } catch (err) {
        // Don't retry on 429 — retrying makes rate limiting worse.
        const isRateLimited = (err as { status?: number }).status === 429;
        if (isRateLimited || attempt >= SESSION_RETRIES) {
          serviceUnavailable = true;
          break;
        }
        await new Promise<void>((r) => setTimeout(r, SESSION_BACKOFF[attempt]));
      }
    }
  }

  if (session === null && !serviceUnavailable) {
    return;
  }

  // Persist session (and carry forward any existing messages/open state).
  if (session) {
    saveState(orgId, widgetId, {
      session,
      messages: persisted?.messages ?? [],
      isOpen: persisted?.isOpen ?? false,
      savedAt: Date.now(),
    });
  }

  // 6. Inject iframe and launcher.
  const iframeSrc = buildIframeSrc(cdnBase);
  const handle = injectWidget(
    iframeSrc,
    config.theme.launcherIconUrl,
    config.theme.primaryColor,
    config.theme.borderRadius,
  );

  handle.launcher.addEventListener("click", () => {
    if (handle.isOpen()) {
      handle.hide();
    } else {
      handle.show();
    }
    persistOpenState();
  });

  // Also persist when the close (X) button is clicked.
  const closeBtn = document.getElementById("__ethos-widget-close__");
  if (closeBtn) {
    closeBtn.addEventListener("click", () => persistOpenState());
  }

  // Restore open state from previous page.
  if (persisted?.isOpen) {
    handle.show();
  }

  function persistOpenState() {
    const current = loadState(orgId, widgetId);
    if (current) {
      saveState(orgId, widgetId, { ...current, isOpen: handle.isOpen(), savedAt: Date.now() });
    }
  }

  // 7. Show approval toolbar in preview mode.
  if (session && session.canPreview && adminBase) {
    handle.showApprovalToolbar(adminBase, widgetId, session.accessToken);
  }

  // 8. Send session + config to iframe via postMessage.
  // Send on iframe load (works in Chrome), and re-send on WIDGET_IFRAME_READY
  // (fixes Safari where load fires before React mounts the message listener).
  const initPayload = {
    type: "WIDGET_INIT" as const,
    session,
    config,
    apiBase,
    hostOrigin: window.location.origin,
    messages: persisted?.messages ?? [],
    selectedDomainSlug: persisted?.selectedDomainSlug,
  };
  handle.iframe.addEventListener("load", () => {
    handle.postMessage(initPayload);
  });

  // 9. Listen for iframe → loader messages (state updates, retry requests).
  window.addEventListener("message", (event: MessageEvent) => {
    if (event.source !== handle.iframe.contentWindow) return;
    const msg = event.data as { type?: string; messages?: unknown[]; slug?: string | null };
    if (!msg?.type) return;

    // Re-send init if iframe signals readiness after the load event.
    if (msg.type === "WIDGET_IFRAME_READY") {
      handle.postMessage(initPayload);
      return;
    }

    if (msg.type === "WIDGET_STATE_UPDATE" && Array.isArray(msg.messages)) {
      const current = loadState(orgId, widgetId);
      saveState(orgId, widgetId, {
        session,
        messages: msg.messages as import("../types/widget.types.js").ChatMessage[],
        isOpen: handle.isOpen(),
        savedAt: Date.now(),
        selectedDomainSlug: current?.selectedDomainSlug,
      });
    }

    if (msg.type === "WIDGET_DOMAIN_SLUG_UPDATE") {
      const current = loadState(orgId, widgetId);
      if (current) {
        saveState(orgId, widgetId, {
          ...current,
          selectedDomainSlug: msg.slug ?? undefined,
          savedAt: Date.now(),
        });
      }
    }

    if (msg.type === "WIDGET_RETRY_SESSION") {
      void (async () => {
        try {
          const retried = await requestSession(apiBase, orgId, widgetId);
          if (retried) {
            session = retried;
            handle.postMessage({ type: "WIDGET_SESSION_REFRESH", session: retried });
            const current = loadState(orgId, widgetId);
            saveState(orgId, widgetId, {
              session: retried,
              messages: current?.messages ?? [],
              isOpen: handle.isOpen(),
              savedAt: Date.now(),
            });
            scheduleRefresh(apiBase, orgId, widgetId, retried, handle);
          } else {
            handle.postMessage({ type: "WIDGET_SESSION_RETRY_FAILED" });
          }
        } catch {
          handle.postMessage({ type: "WIDGET_SESSION_RETRY_FAILED" });
        }
      })();
    }
  });

  // 10. Schedule proactive session refresh at 80% of TTL.
  if (session) {
    scheduleRefresh(apiBase, orgId, widgetId, session, handle);
  }
}

/**
 * Decode widget key to orgId + widgetId.
 * V1 convention: base64url("{orgId}:{widgetId}")
 */
/** Decode a widget key (wk_<base64url of orgId:widgetId>) into its parts. */
function resolveWidgetKey(key: string): { orgId: string; widgetId: string } | null {
  try {
    // Strip the wk_ prefix
    const raw = key.startsWith("wk_") ? key.slice(3) : key;
    // base64url → base64 → decode
    const decoded = atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
    const separatorIdx = decoded.indexOf(":");
    if (separatorIdx === -1) return null;
    const orgId = decoded.slice(0, separatorIdx);
    const widgetId = decoded.slice(separatorIdx + 1);
    if (!orgId || !widgetId) return null;
    return { orgId, widgetId };
  } catch {
    return null;
  }
}

/** Build the iframe src URL from the CDN base. */
function buildIframeSrc(cdnBase: string): string {
  return `${cdnBase}/widget-assets/iframe/iframe.html`;
}

/** Extract __wpreview query param value from the current URL (returns null if absent). */
function extractPreviewToken(): string | null {
  const url = new URL(window.location.href);
  return url.searchParams.get("__wpreview");
}

/** Strip __wpreview query param from the current URL without reloading. */
function stripPreviewParam(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has("__wpreview")) {
    url.searchParams.delete("__wpreview");
    history.replaceState(null, "", url.toString());
  }
}

/**
 * Schedule a proactive token refresh at 80% of the session TTL.
 * On success: posts new session to iframe and reschedules.
 * On failure: posts WIDGET_SESSION_EXPIRED so iframe shows the expiry message.
 */
function scheduleRefresh(
  apiBase: string,
  orgId: string,
  widgetId: string,
  session: SessionResponse,
  handle: { postMessage: (msg: unknown) => void },
): void {
  const expiresAt = new Date(session.expiresAt).getTime();
  const ttlMs = expiresAt - Date.now();
  if (ttlMs <= 0) return;

  setTimeout(() => {
    void (async () => {
      const refreshed = await refreshSession(apiBase, orgId, widgetId, session.accessToken);
      if (refreshed) {
        handle.postMessage({ type: "WIDGET_SESSION_REFRESH", session: refreshed });
        // Persist the refreshed session so it survives page navigations.
        const current = loadState(orgId, widgetId);
        saveState(orgId, widgetId, {
          session: refreshed,
          messages: current?.messages ?? [],
          isOpen: current?.isOpen ?? false,
          savedAt: Date.now(),
        });
        scheduleRefresh(apiBase, orgId, widgetId, refreshed, handle);
      } else {
        handle.postMessage({ type: "WIDGET_SESSION_EXPIRED" });
        clearState(orgId, widgetId);
      }
    })();
  }, Math.floor(ttlMs * 0.8));
}
